import { useEffect } from 'react'
import { useNavigate } from 'react-router'
import { usePostHog } from '@posthog/react'
import { Button } from '@heroui/react'
import { useTheme } from '@/ui/theme'
import { RefundMethod } from '@itsrever/returns-api-types'
import { RefundMethodCard } from '@/app/refund-methods/v2/refund-method-card'
import { useCalculate, usePages, useReturnMethods, useTranslate } from '@/hooks'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import {
  FraudGroup,
  FraudGroups,
  FraudScore,
  FraudScores,
  RefundActions,
  RefundPaymentMethod,
  RefundPaymentMethods,
  ReturnMethod,
  setKeepYourItem,
  setRefundPaymentMethod,
  setReturnMethod
} from '@/redux/return/return-slice'
import { LoadingOverlay } from '@/ui/components/loading-overlay'
import FallbackNotFound from '@/app/return-methods/fallback-not-found'
import { isWFPClaim } from '@/utils/worry-free-purchase'

function isGiftCardOrPromoCode(method: RefundMethod) {
  return (
    method.method === RefundPaymentMethod.GiftCard ||
    method.method === RefundPaymentMethod.PromoCode
  )
}

export const RefundMethodsPage = () => {
  const { translate } = useTranslate()

  const theme = useTheme()

  const posthog = usePostHog()

  const dispatch = useAppDispatch()

  const navigate = useNavigate()
  const { pages } = usePages()

  const { callCalculate, loadingCalculate } = useCalculate()
  const { callReturnMethods } = useReturnMethods(() =>
    navigate(pages.ReturnMethod)
  )

  const checkoutId = useAppSelector(
    (store) => store.returnsApi.order.response?.checkout_id
  )

  const order = useAppSelector(
    (store) => store.returnsApi.order.response?.order
  )

  const settings = useAppSelector(
    (store) => store.settingsApi.settings.response
  )

  const isInStore = settings.in_store_settings?.is_enabled

  const orderReturn = useAppSelector((store) => store.return)

  const refundMethodsResponse = useAppSelector(
    (store) => store.refundMethodsApi.refundMethods.response
  )

  const refundMethods = refundMethodsResponse.result?.refund_methods

  const isGiftCardOffered =
    refundMethods?.some(
      ({ method }) =>
        method === RefundPaymentMethod.GiftCard ||
        method === RefundPaymentMethod.PromoCode
    ) ?? false

  const handleContinue = () => {
    posthog.capture('Refund Method', {
      RefundPaymentMethod:
        RefundPaymentMethods[
          orderReturn.refundPaymentMethod.method ??
            RefundPaymentMethod.NoPaymentMethod
        ],
      FraudScore:
        FraudScores[
          refundMethodsResponse.result?.fraud_result?.outcome as FraudScore
        ],
      FraudGroup:
        FraudGroups[
          refundMethodsResponse.result?.fraud_result?.group as FraudGroup
        ],
      checkout_id: checkoutId
    })

    const notReceivedOnly = orderReturn.returnedItems.every(
      (retItem) =>
        retItem.item.product_return_reason == 'NOT_RECEIVED' ||
        retItem.item.product_return_reason == 'PACKAGE_NOT_ARRIVED'
    )

    const hasExchanges = orderReturn.returnedItems.some(
      (item) => item.item.action === RefundActions.ToExchange
    )

    if (isWFPClaim(order?.line_items ?? [], orderReturn.returnedItems)) {
      callCalculate(
        ReturnMethod.NoReturnMethod,
        hasExchanges ? orderReturn.returnAddress : undefined
      )
      return
    }

    if (isInStore) {
      dispatch(
        setReturnMethod({
          returnMethod: notReceivedOnly
            ? ReturnMethod.NoReturnMethod
            : ReturnMethod.InStore,
          userSelectedCarrier: '',
          userSelectedProvider: ''
        })
      )

      callCalculate(
        notReceivedOnly ? ReturnMethod.NoReturnMethod : ReturnMethod.InStore
      )

      return
    }

    if (notReceivedOnly) {
      if (hasExchanges) {
        callCalculate(ReturnMethod.NoReturnMethod, orderReturn.returnAddress)
      } else {
        callCalculate(ReturnMethod.NoReturnMethod)
      }
      return
    } else {
      callReturnMethods(orderReturn.returnAddress)
    }
  }

  useEffect(() => {
    if (refundMethodsResponse.result?.fraud_result) {
      posthog.capture('Fraud Score Result', {
        FraudScore:
          FraudScores[
            refundMethodsResponse.result.fraud_result.outcome as FraudScore
          ],
        FraudGroup:
          FraudGroups[
            refundMethodsResponse.result.fraud_result.group as FraudGroup
          ],
        checkout_id: checkoutId
      })
    }

    if (refundMethods) {
      posthog.capture('Refund Method Page Loaded', {
        RefundMethodsOffered: refundMethods?.map(
          (method) => method.method ?? 0
        ),
        GiftCardOffered: isGiftCardOffered,
        GiftCardRDVSurplus: isGiftCardOffered
          ? refundMethods.some(
              (method) =>
                isGiftCardOrPromoCode(method) &&
                ((method.rdv_surplus?.surplus_amount ?? 0) > 0 ||
                  (method.rdv_surplus?.surplus_percentage ?? 0) > 0)
            )
          : false,
        GiftCardFreeShipping: isGiftCardOffered
          ? refundMethods.some(
              (method) =>
                isGiftCardOrPromoCode(method) &&
                parseFloat(method.shipping_costs?.max?.amount || '0') === 0
            )
          : false,
        isKeepYourItem: orderReturn.keepYourItem,
        checkout_id: checkoutId
      })
    }
  }, [
    posthog,
    refundMethodsResponse.result?.fraud_result,
    isGiftCardOffered,
    refundMethods,
    orderReturn.keepYourItem,
    checkoutId
  ])

  return (
    <>
      {loadingCalculate ? <LoadingOverlay loading /> : null}
      <div className="flex-1 bg-white lg:bg-[#f2f2f2]">
        <main className="flex items-center justify-center pb-3 pt-6">
          <div className="relative size-full max-w-lg rounded-lg bg-white lg:px-8 lg:py-6 lg:shadow-sm">
            <div className="px-4 md:px-0">
              <h1
                className="text-center text-lg font-normal text-neutral-800"
                dangerouslySetInnerHTML={{
                  __html: translate('refund_page_title')
                }}
              />
              {refundMethods && refundMethods.length > 0 ? (
                <ul className="pb-6 pt-8">
                  <div className="flex flex-col gap-4">
                    {refundMethods?.map((refundMethod) => {
                      if (refundMethod) {
                        return (
                          <li
                            id={refundMethod.method?.toString()}
                            key={refundMethod.method}
                            onClick={() => {
                              dispatch(setRefundPaymentMethod(refundMethod))
                              dispatch(setKeepYourItem(false))
                            }}
                          >
                            <RefundMethodCard
                              method={refundMethod}
                              isSelected={
                                refundMethod.method ===
                                orderReturn.refundPaymentMethod.method
                              }
                              isGiftCardOffered={isGiftCardOffered}
                            />
                          </li>
                        )
                      }

                      return null
                    })}
                  </div>
                </ul>
              ) : (
                <div className="pt-6">
                  <FallbackNotFound title="refund_page_no_methods_available" />
                </div>
              )}
            </div>
            <div className="sticky bottom-0 flex w-full justify-center bg-white px-4 py-2.5 md:relative md:p-0">
              <Button
                id="refundMethodContinue"
                fullWidth
                onPress={handleContinue}
                isDisabled={
                  orderReturn.refundPaymentMethod.method === 0 ||
                  refundMethods?.length === 0
                }
                variant="solid"
                radius="sm"
                size="lg"
                style={{
                  backgroundColor: theme.colors.primary.main,
                  color: theme.colors.primary.contrastText,
                  borderRadius: theme.borderRadius
                }}
              >
                {translate('refund_page_button_continue')}
              </Button>
            </div>
          </div>
        </main>
      </div>
    </>
  )
}
